import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, imgSrc } from "@/lib/api";
import { clearNavCache } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";

export default function Admin() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // preview + confirm flow
  const [pendingFile, setPendingFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [asDraft, setAsDraft] = useState(true);
  const [skipDuplicates, setSkipDuplicates] = useState(false);

  // bulk selection
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Page social/OG settings (Home + Map)
  const [settings, setSettings] = useState({
    home_og_title: "", home_og_description: "", home_og_image: "",
    map_og_title: "", map_og_description: "", map_og_image: "",
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [ogTarget, setOgTarget] = useState(null); // null | "home" | "map"
  const ogFileRef = useRef(null);

  const fileRef = useRef(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isOwner = !!user?.is_owner;

  // Editable main navigation (owner only)
  const [navOpen, setNavOpen] = useState(false);
  const [navRows, setNavRows] = useState([]);
  const [navSaving, setNavSaving] = useState(false);
  const [navSaved, setNavSaved] = useState(false);
  const [navError, setNavError] = useState("");

  // Visitor questions ("Ask anything" box)
  const [questionsOpen, setQuestionsOpen] = useState(false);
  const [questions, setQuestions] = useState(null);
  const [qFilter, setQFilter] = useState("open"); // open | answered | all
  const [answerFor, setAnswerFor] = useState(null); // question id being answered
  const [answerText, setAnswerText] = useState("");
  const [answerSaving, setAnswerSaving] = useState(false);

  // Ask-page email subscribers
  const [subsOpen, setSubsOpen] = useState(false);
  const [subs, setSubs] = useState(null);

  const openSubscribers = async () => {
    setSubsOpen(true);
    setSubs(null);
    try {
      const r = await api.get("/admin/subscribers");
      setSubs(r.data);
    } catch {
      setSubs([]);
    }
    api.post("/admin/activity/view", { section: "subscribers" }).catch(() => {});
  };

  const onDeleteSubscriber = async (sid) => {
    if (!window.confirm("Remove this subscriber?")) return;
    await api.delete(`/admin/subscribers/${sid}`);
    setSubs((ss) => ss.filter((s) => s.id !== sid));
  };

  // Listing edit suggestions ("Own this business?")
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [sugFilter, setSugFilter] = useState("open"); // open | handled | all

  const openSuggestions = async () => {
    setSuggestionsOpen(true);
    setSuggestions(null);
    try {
      const r = await api.get("/admin/suggestions");
      setSuggestions(r.data);
    } catch {
      setSuggestions([]);
    }
    api.post("/admin/activity/view", { section: "suggestions" }).catch(() => {});
  };

  const onDeleteSuggestion = async (sid) => {
    if (!window.confirm("Delete this suggestion?")) return;
    await api.delete(`/admin/suggestions/${sid}`);
    setSuggestions((ss) => ss.filter((s) => s.id !== sid));
  };

  const onToggleSuggestionHandled = async (s) => {
    const next = s.status === "handled" ? "new" : "handled";
    await api.put(`/admin/suggestions/${s.id}/status`, { status: next });
    setSuggestions((ss) => ss.map((x) => x.id === s.id ? { ...x, status: next } : x));
  };

  const saveAnswer = async (qid) => {
    setAnswerSaving(true);
    try {
      const answer = answerText.trim();
      await api.put(`/admin/questions/${qid}/answer`, { answer });
      setQuestions((qs) => qs.map((q) => q.id === qid ? { ...q, answer, status: answer ? "answered" : "new" } : q));
      setAnswerFor(null);
    } catch { /* ignore */ } finally {
      setAnswerSaving(false);
    }
  };

  const onDeleteQuestion = async (qid) => {
    if (!window.confirm("Delete this question? This cannot be undone.")) return;
    await api.delete(`/admin/questions/${qid}`);
    setQuestions((qs) => qs.filter((q) => q.id !== qid));
    if (answerFor === qid) setAnswerFor(null);
  };

  const openNavEditor = async () => {
    setNavError("");
    setNavSaved(false);
    try {
      const r = await api.get("/nav");
      setNavRows((r.data.items || []).map((it, i) => ({ key: `n${i}`, label: it.label, url: it.url })));
      setNavOpen(true);
      api.post("/admin/activity/view", { section: "nav" }).catch(() => {});
    } catch { /* ignore */ }
  };

  const saveNav = async () => {
    const items = navRows.map((r) => ({ label: r.label.trim(), url: r.url.trim() })).filter((r) => r.label && r.url);
    if (!items.length) return setNavError("Add at least one menu item with a label and URL.");
    setNavSaving(true);
    setNavError("");
    try {
      await api.put("/admin/nav", { items });
      clearNavCache();
      setNavSaved(true);
      setTimeout(() => { setNavSaved(false); setNavOpen(false); }, 900);
    } catch (e) {
      setNavError(e?.response?.data?.detail || "Failed to save the menu.");
    } finally {
      setNavSaving(false);
    }
  };

  const openQuestions = async () => {
    setQuestionsOpen(true);
    setQuestions(null);
    try {
      const r = await api.get("/admin/questions");
      setQuestions(r.data);
    } catch {
      setQuestions([]);
    }
    api.post("/admin/activity/view", { section: "questions" }).catch(() => {});
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/posts");
      setPosts(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.post("/admin/activity/view", { section: "posts" }).catch(() => {});
  }, []);

  const onOgImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !ogTarget) return;
    const fd = new FormData();
    fd.append("file", f);
    const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setSettings((s) => ({ ...s, [`${ogTarget}_og_image`]: r.data.url }));
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    setSettingsSaved(false);
    try {
      const r = await api.put("/admin/settings", settings);
      setSettings(r.data);
      setSettingsSaved(true);
      setTimeout(() => {
        setSettingsSaved(false);
        setOgTarget(null);
      }, 900);
    } finally {
      setSavingSettings(false);
    }
  };

  // ---------- Import: pick file -> dry-run preview ----------
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", "true");
      fd.append("as_draft", "false");
      const res = await api.post("/admin/posts/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPendingFile(file);
      setAsDraft(true);
      setSkipDuplicates((res.data.duplicate_count || 0) > 0);
      setPreview(res.data);
    } catch (err) {
      setImportResult({ error: err?.response?.data?.detail || "Could not read the file." });
    } finally {
      setImporting(false);
    }
  };

  const confirmImport = async () => {
    if (!pendingFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("as_draft", asDraft ? "true" : "false");
      fd.append("skip_duplicates", skipDuplicates ? "true" : "false");
      const res = await api.post("/admin/posts/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(res.data);
      setPreview(null);
      setPendingFile(null);
      await load();
    } catch (err) {
      setImportResult({ error: err?.response?.data?.detail || "Import failed." });
      setPreview(null);
      setPendingFile(null);
    } finally {
      setImporting(false);
    }
  };

  const cancelPreview = () => {
    setPreview(null);
    setPendingFile(null);
  };

  const downloadTemplate = () => {
    const csv =
      "title,excerpt,body,categories,tags,status,featured_image,publish_date,phone,location_url\n" +
      'My First Post,A short summary of the post,"Write your first paragraph here.\n\nLeave a blank line to start a new paragraph.",Work;Studio,portfolio;design,published,https://images.unsplash.com/photo-1500000000000,2026-06-01,+1 555 123 4567,https://maps.google.com/?q=San+Juanillo\n' +
      'A Draft Post,,"<p>You can also paste raw HTML.</p>",Craft,notes,draft,,,,\n';
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "posts-import-template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ---------- Bulk selection ----------
  const allSelected = posts.length > 0 && selected.size === posts.length;
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(posts.map((p) => p.id)));
  };
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${ids.length} selected post(s)? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      await api.post("/admin/posts/bulk", { ids, action });
      clearSelection();
      await load();
    } catch (err) {
      setImportResult({ error: err?.response?.data?.detail || "Bulk action failed." });
    } finally {
      setBulkBusy(false);
    }
  };

  const onDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    await api.delete(`/admin/posts/${id}`);
    load();
  };

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const selectedCount = selected.size;

  return (
    <div className="admin" data-testid="admin-page">
      <header className="admin-header">
        <div className="admin-header-inner">
          <div className="admin-brand">San Juanillo Rising &middot; Admin</div>
          <div className="admin-header-actions">
            {isOwner ? (
              <button className="admin-link admin-link-btn" onClick={openNavEditor} data-testid="menu-btn">Menu</button>
            ) : null}
            <button className="admin-link admin-link-btn" onClick={openQuestions} data-testid="questions-btn">Questions</button>
            <button className="admin-link admin-link-btn" onClick={openSuggestions} data-testid="suggestions-btn">Suggestions</button>
            <button className="admin-link admin-link-btn" onClick={openSubscribers} data-testid="subscribers-btn">Subscribers</button>
            <a href="/" target="_blank" rel="noreferrer" className="admin-link">View site</a>
            <Link to="/admin/analytics" className="admin-link" data-testid="analytics-link">Analytics</Link>
            <span className="admin-user">{user?.email}</span>
            <button className="btn-ghost" onClick={doLogout} data-testid="logout-btn">Log out</button>
          </div>
        </div>
      </header>

      <main className="admin-main">
        <div className="admin-toolbar">
          <h1>Posts</h1>
          <div className="admin-toolbar-actions">
            <button className="btn-ghost" onClick={downloadTemplate} data-testid="download-template-btn">
              Download CSV template
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onImportFile}
              style={{ display: "none" }}
              data-testid="import-file-input"
            />
            <button
              className="btn-ghost"
              onClick={() => fileRef.current?.click()}
              disabled={importing}
              data-testid="import-spreadsheet-btn"
            >
              {importing && !preview ? "Reading…" : "Import spreadsheet"}
            </button>
            <Link to="/admin/posts/new" className="btn-primary" data-testid="new-post-btn">+ New Post</Link>
          </div>
        </div>

        {importResult ? (
          <div className={`admin-import-result${importResult.error ? " is-error" : ""}`} data-testid="import-result">
            <button className="admin-import-dismiss" onClick={() => setImportResult(null)} aria-label="Dismiss" data-testid="import-dismiss">×</button>
            {importResult.error ? (
              <span>{importResult.error}</span>
            ) : (
              <div>
                <strong>Imported {importResult.created_count} post{importResult.created_count === 1 ? "" : "s"}</strong>
                {importResult.skipped_count ? ` · ${importResult.skipped_count} duplicate(s) skipped` : ""}
                {importResult.error_count ? ` · ${importResult.error_count} row(s) skipped` : ""}.
                {importResult.error_count ? (
                  <ul className="admin-import-errors">
                    {importResult.errors.map((er, i) => (<li key={i}>Row {er.row}: {er.error}</li>))}
                  </ul>
                ) : null}
              </div>
            )}
          </div>
        ) : null}

        {selectedCount > 0 ? (
          <div className="admin-bulk-bar" data-testid="bulk-bar">
            <span className="admin-bulk-count">{selectedCount} selected</span>
            <div className="admin-bulk-actions">
              <button className="btn-ghost" disabled={bulkBusy} onClick={() => runBulk("publish")} data-testid="bulk-publish-btn">Publish</button>
              <button className="btn-ghost" disabled={bulkBusy} onClick={() => runBulk("unpublish")} data-testid="bulk-unpublish-btn">Unpublish</button>
              <button className="btn-danger" disabled={bulkBusy} onClick={() => runBulk("delete")} data-testid="bulk-delete-btn">Delete</button>
              <button className="admin-bulk-clear" onClick={clearSelection} data-testid="bulk-clear-btn">Clear</button>
            </div>
          </div>
        ) : null}

        {loading ? (
          <p>Loading&hellip;</p>
        ) : (
          <table className="admin-table" data-testid="posts-table">
            <thead>
              <tr>
                <th className="admin-check-cell">
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} aria-label="Select all" data-testid="select-all-checkbox" />
                </th>
                <th></th>
                <th>Title</th>
                <th>Status</th>
                <th>Date</th>
                <th>Categories</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr className="admin-home-row" data-testid="home-og-row">
                <td className="admin-check-cell" aria-hidden="true"></td>
                <td className="admin-thumb-cell">
                  {settings.home_og_image ? <img src={imgSrc(settings.home_og_image)} alt="" data-testid="og-image-preview" /> : <div className="admin-thumb-ph" />}
                </td>
                <td>
                  <span className="admin-title-link">Home page</span>
                  <div className="admin-slug">Social preview (OG)</div>
                </td>
                <td><span className="badge badge-static">home</span></td>
                <td>&mdash;</td>
                <td>&mdash;</td>
                <td className="admin-actions">
                  <button className="btn-ghost" onClick={() => setOgTarget("home")} data-testid="edit-home-og">Edit</button>
                </td>
              </tr>
              <tr className="admin-home-row" data-testid="map-og-row">
                <td className="admin-check-cell" aria-hidden="true"></td>
                <td className="admin-thumb-cell">
                  {settings.map_og_image ? <img src={imgSrc(settings.map_og_image)} alt="" data-testid="map-og-image-preview" /> : <div className="admin-thumb-ph" />}
                </td>
                <td>
                  <span className="admin-title-link">Map page</span>
                  <div className="admin-slug">Social preview (OG)</div>
                </td>
                <td><span className="badge badge-static">map</span></td>
                <td>&mdash;</td>
                <td>&mdash;</td>
                <td className="admin-actions">
                  <button className="btn-ghost" onClick={() => setOgTarget("map")} data-testid="edit-map-og">Edit</button>
                </td>
              </tr>
              {posts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="admin-empty">No posts yet. Create your first one.</td>
                </tr>
              ) : posts.map((p) => (
                <tr key={p.id} data-testid={`post-row-${p.slug}`} className={selected.has(p.id) ? "is-selected" : ""}>
                  <td className="admin-check-cell">
                    <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} aria-label={`Select ${p.title}`} data-testid={`select-${p.slug}`} />
                  </td>
                  <td className="admin-thumb-cell">
                    {p.featured_image ? <img src={imgSrc(p.featured_image)} alt="" /> : <div className="admin-thumb-ph" />}
                  </td>
                  <td>
                    <Link to={`/admin/posts/${p.id}/edit`} className="admin-title-link">{p.title}</Link>
                    <div className="admin-slug">/{p.slug}</div>
                    {p.submitter_email ? <div className="admin-submitter" data-testid={`submitter-${p.slug}`}>Submitted by {p.submitter_email}</div> : null}
                  </td>
                  <td><span className={`badge badge-${p.status}`}>{p.status}</span></td>
                  <td>{(p.publish_date || "").slice(0, 10)}</td>
                  <td>{(p.categories || []).join(", ")}</td>
                  <td className="admin-actions">
                    <a href={`/preview/${p.id}`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid={`preview-${p.slug}`}>Preview</a>
                    <Link to={`/admin/posts/${p.id}/edit`} className="btn-ghost" data-testid={`edit-${p.slug}`}>Edit</Link>
                    <button className="btn-danger" onClick={() => onDelete(p.id, p.title)} data-testid={`delete-${p.slug}`}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </main>

      {ogTarget ? (
        <div className="admin-modal-overlay" onClick={() => setOgTarget(null)} data-testid={`${ogTarget}-og-modal`}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} data-testid={`${ogTarget}-og-settings`}>
            <div className="admin-modal-head">
              <h2>{ogTarget === "map" ? "Map" : "Home"} page social preview (OG)</h2>
              <button className="admin-import-dismiss" onClick={() => setOgTarget(null)} aria-label="Close" data-testid="og-close">×</button>
            </div>
            <p className="admin-modal-sub">Controls the title, description &amp; image shown when your {ogTarget === "map" ? "map" : "home"} page is shared on social apps.</p>
            <div className="admin-og-grid">
              <div className="admin-og-fields">
                <label className="editor-label">OG title</label>
                <input
                  className="editor-input"
                  value={settings[`${ogTarget}_og_title`] || ""}
                  onChange={(e) => setSettings((s) => ({ ...s, [`${ogTarget}_og_title`]: e.target.value }))}
                  placeholder={ogTarget === "map" ? "Map — San Juanillo Rising" : "San Juanillo Rising"}
                  data-testid="og-title-input"
                />
                <label className="editor-label">OG description</label>
                <textarea
                  className="editor-input"
                  rows={3}
                  value={settings[`${ogTarget}_og_description`] || ""}
                  onChange={(e) => setSettings((s) => ({ ...s, [`${ogTarget}_og_description`]: e.target.value }))}
                  placeholder="A short sentence describing this page."
                  data-testid="og-description-input"
                />
              </div>
              <div className="admin-og-image">
                <label className="editor-label">OG image</label>
                {settings[`${ogTarget}_og_image`] ? (
                  <img src={imgSrc(settings[`${ogTarget}_og_image`])} alt="OG preview" data-testid="og-image-modal-preview" />
                ) : (
                  <div className="admin-og-image-ph">No image</div>
                )}
                <input ref={ogFileRef} type="file" accept="image/*" onChange={onOgImage} style={{ display: "none" }} data-testid="og-image-input" />
                <button className="btn-ghost btn-small" onClick={() => ogFileRef.current?.click()} data-testid="og-image-upload">
                  {settings[`${ogTarget}_og_image`] ? "Replace image" : "Upload image"}
                </button>
              </div>
            </div>
            <div className="admin-modal-actions">
              <button className="btn-ghost" onClick={() => setOgTarget(null)} data-testid="og-cancel-btn">Cancel</button>
              <button className="btn-primary" onClick={saveSettings} disabled={savingSettings} data-testid="save-og-btn">
                {savingSettings ? "Saving…" : settingsSaved ? "Saved ✓" : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {navOpen ? (
        <div className="admin-modal-overlay" onClick={() => setNavOpen(false)} data-testid="nav-editor-modal">
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} data-testid="nav-editor">
            <div className="admin-modal-head">
              <h2>Main navigation</h2>
              <button className="admin-import-dismiss" onClick={() => setNavOpen(false)} aria-label="Close" data-testid="nav-close">×</button>
            </div>
            <p className="admin-modal-sub">Edit the menu shown at the top of the site. Use a full URL (https://…) for external links; internal pages start with /.</p>
            {navRows.map((row, i) => (
              <div className="admin-nav-row" key={row.key}>
                <input
                  className="editor-input"
                  value={row.label}
                  placeholder="Label"
                  onChange={(e) => setNavRows((rows) => rows.map((r) => r.key === row.key ? { ...r, label: e.target.value } : r))}
                  data-testid={`nav-label-${i}`}
                />
                <input
                  className="editor-input"
                  value={row.url}
                  placeholder="/map or https://…"
                  onChange={(e) => setNavRows((rows) => rows.map((r) => r.key === row.key ? { ...r, url: e.target.value } : r))}
                  data-testid={`nav-url-${i}`}
                />
                <button className="btn-ghost btn-small" onClick={() => setNavRows((rows) => rows.filter((r) => r.key !== row.key))} data-testid={`nav-delete-${i}`}>Delete</button>
              </div>
            ))}
            <button className="btn-ghost btn-small" onClick={() => setNavRows((rows) => [...rows, { key: `n${Date.now()}`, label: "", url: "" }])} data-testid="nav-add-btn">+ Add item</button>
            {navError ? <div className="admin-nav-error" data-testid="nav-save-error">{navError}</div> : null}
            <div className="admin-modal-actions">
              <button className="btn-ghost" onClick={() => setNavOpen(false)} data-testid="nav-cancel-btn">Cancel</button>
              <button className="btn-primary" onClick={saveNav} disabled={navSaving} data-testid="nav-save-btn">
                {navSaving ? "Saving…" : navSaved ? "Saved ✓" : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {questionsOpen ? (
        <div className="admin-modal-overlay" onClick={() => setQuestionsOpen(false)} data-testid="questions-modal">
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-head">
              <h2>Visitor questions</h2>
              <button className="admin-import-dismiss" onClick={() => setQuestionsOpen(false)} aria-label="Close" data-testid="questions-close">×</button>
            </div>
            <p className="admin-modal-sub">Questions sent through the "Ask anything about San Juanillo" box on the site.</p>
            {questions !== null && questions.length > 0 ? (
              <div className="admin-sug-filters" data-testid="questions-filters">
                {[["open", "Open"], ["answered", "Answered"], ["all", "All"]].map(([key, label]) => (
                  <button
                    key={key}
                    className={`admin-sug-filter${qFilter === key ? " is-active" : ""}`}
                    onClick={() => setQFilter(key)}
                    data-testid={`questions-filter-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
            {questions === null ? (
              <p>Loading…</p>
            ) : questions.length === 0 ? (
              <p className="admin-questions-empty" data-testid="questions-empty">No questions yet.</p>
            ) : questions.filter((q) => qFilter === "all" || (qFilter === "answered" ? q.status === "answered" : q.status !== "answered")).length === 0 ? (
              <p className="admin-questions-empty" data-testid="questions-empty-filtered">
                {qFilter === "answered" ? "Nothing answered yet." : "No open questions."}
              </p>
            ) : (
              <div className="admin-questions-list" data-testid="questions-list">
                {questions
                  .filter((q) => qFilter === "all" || (qFilter === "answered" ? q.status === "answered" : q.status !== "answered"))
                  .map((q, i) => (
                  <div className="admin-question-row" key={q.id} data-testid={`question-row-${i}`}>
                    <div className="admin-question-text">{q.question}</div>
                    <div className="admin-question-meta">
                      {(q.created_at || "").slice(0, 10)} · {q.email ? q.email : "no email left"}
                      {q.status === "answered" ? " · answered" : ""}
                    </div>
                    {q.answer && answerFor !== q.id ? (
                      <div className="admin-question-answer" data-testid={`question-answer-${i}`}>{q.answer}</div>
                    ) : null}
                    {answerFor === q.id ? (
                      <div className="admin-answer-form">
                        <textarea
                          className="editor-input"
                          rows={2}
                          value={answerText}
                          onChange={(e) => setAnswerText(e.target.value)}
                          placeholder="Write the answer — it will appear on the Ask page."
                          data-testid={`answer-input-${i}`}
                        />
                        <div className="admin-answer-actions">
                          <button className="btn-primary btn-small" onClick={() => saveAnswer(q.id)} disabled={answerSaving} data-testid={`answer-save-${i}`}>
                            {answerSaving ? "Saving…" : "Publish answer"}
                          </button>
                          <button className="btn-ghost btn-small" onClick={() => setAnswerFor(null)} data-testid={`answer-cancel-${i}`}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="admin-question-actions">
                        <button
                          className="btn-ghost btn-small"
                          onClick={() => { setAnswerFor(q.id); setAnswerText(q.answer || ""); }}
                          data-testid={`question-answer-btn-${i}`}
                        >
                          {q.answer ? "Edit answer" : "Answer"}
                        </button>
                        <button
                          className="btn-danger btn-small"
                          onClick={() => onDeleteQuestion(q.id)}
                          data-testid={`question-delete-${i}`}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {suggestionsOpen ? (
        <div className="admin-modal-overlay" onClick={() => setSuggestionsOpen(false)} data-testid="suggestions-modal">
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-head">
              <h2>Edit suggestions</h2>
              <button className="admin-import-dismiss" onClick={() => setSuggestionsOpen(false)} aria-label="Close" data-testid="suggestions-close">×</button>
            </div>
            <p className="admin-modal-sub">Corrections suggested by business owners via "Own this business? Suggest an edit" on each listing.</p>
            {suggestions !== null && suggestions.length > 0 ? (
              <div className="admin-sug-filters" data-testid="suggestions-filters">
                {[["open", "Open"], ["handled", "Handled"], ["all", "All"]].map(([key, label]) => (
                  <button
                    key={key}
                    className={`admin-sug-filter${sugFilter === key ? " is-active" : ""}`}
                    onClick={() => setSugFilter(key)}
                    data-testid={`suggestions-filter-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
            {suggestions === null ? (
              <p>Loading…</p>
            ) : suggestions.length === 0 ? (
              <p className="admin-questions-empty" data-testid="suggestions-empty">No suggestions yet.</p>
            ) : suggestions.filter((s) => sugFilter === "all" || (sugFilter === "handled" ? s.status === "handled" : s.status !== "handled")).length === 0 ? (
              <p className="admin-questions-empty" data-testid="suggestions-empty-filtered">
                {sugFilter === "handled" ? "Nothing handled yet." : "No open suggestions."}
              </p>
            ) : (
              <div className="admin-questions-list" data-testid="suggestions-list">
                {suggestions
                  .filter((s) => sugFilter === "all" || (sugFilter === "handled" ? s.status === "handled" : s.status !== "handled"))
                  .map((s, i) => (
                  <div className={`admin-question-row${s.status === "handled" ? " is-handled" : ""}`} key={s.id} data-testid={`suggestion-row-${i}`}>
                    <div className="admin-question-text">
                      {s.post_title}
                      {s.status === "handled" ? <span className="admin-handled-pill" data-testid={`suggestion-handled-${i}`}>Handled</span> : null}
                    </div>
                    <div className="admin-question-meta">
                      {(s.created_at || "").slice(0, 10)} · {[s.name, s.email].filter(Boolean).join(" · ") || "anonymous"}
                    </div>
                    <div className="admin-question-answer">{s.message}</div>
                    <div className="admin-question-actions">
                      <button className="btn-ghost btn-small" onClick={() => onToggleSuggestionHandled(s)} data-testid={`suggestion-handled-btn-${i}`}>
                        {s.status === "handled" ? "Reopen" : "Mark handled"}
                      </button>
                      <button className="btn-danger btn-small" onClick={() => onDeleteSuggestion(s.id)} data-testid={`suggestion-delete-${i}`}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {subsOpen ? (
        <div className="admin-modal-overlay" onClick={() => setSubsOpen(false)} data-testid="subscribers-modal">
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-head">
              <h2>Ask subscribers</h2>
              <button className="admin-import-dismiss" onClick={() => setSubsOpen(false)} aria-label="Close" data-testid="subscribers-close">×</button>
            </div>
            <p className="admin-modal-sub">Visitors who subscribed to new-answer notifications on the Ask page.</p>
            {subs === null ? (
              <p>Loading…</p>
            ) : subs.length === 0 ? (
              <p className="admin-questions-empty" data-testid="subscribers-empty">No subscribers yet.</p>
            ) : (
              <div className="admin-questions-list" data-testid="subscribers-list">
                {subs.map((s, i) => (
                  <div className="admin-question-row" key={s.id} data-testid={`subscriber-row-${i}`}>
                    <div className="admin-question-text">{s.email}</div>
                    <div className="admin-question-meta">{(s.created_at || "").slice(0, 10)}</div>
                    <div className="admin-question-actions">
                      <button className="btn-danger btn-small" onClick={() => onDeleteSubscriber(s.id)} data-testid={`subscriber-delete-${i}`}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {preview ? (
        <div className="admin-modal-overlay" onClick={cancelPreview} data-testid="import-preview-modal">
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-head">
              <h2>Review import</h2>
              <button className="admin-import-dismiss" onClick={cancelPreview} aria-label="Close" data-testid="preview-close">×</button>
            </div>
            <p className="admin-modal-sub" data-testid="preview-summary">
              {preview.valid_count} post{preview.valid_count === 1 ? "" : "s"} ready to import
              {preview.error_count ? ` · ${preview.error_count} row(s) will be skipped` : ""}.
            </p>

            <label className="admin-draft-toggle" data-testid="as-draft-toggle">
              <input type="checkbox" checked={asDraft} onChange={(e) => setAsDraft(e.target.checked)} data-testid="as-draft-checkbox" />
              Import all as drafts (review before publishing)
            </label>

            {preview.duplicate_count ? (
              <div className="admin-dup-warn" data-testid="duplicate-warning">
                <div>
                  <strong>{preview.duplicate_count} row(s) look like duplicates</strong> of posts that already exist (matching title or slug).
                </div>
                <label className="admin-dup-toggle" data-testid="skip-duplicates-toggle">
                  <input type="checkbox" checked={skipDuplicates} onChange={(e) => setSkipDuplicates(e.target.checked)} data-testid="skip-duplicates-checkbox" />
                  Skip duplicates
                </label>
              </div>
            ) : null}

            <div className="admin-preview-scroll">
              <table className="admin-table admin-preview-table">
                <thead>
                  <tr><th>Row</th><th>Title</th><th>Slug</th><th>Status</th><th>Categories</th><th>Body</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  {preview.preview.map((r) => (
                    <tr key={r.row} data-testid={`preview-row-${r.row}`} className={r.duplicate ? "is-duplicate" : ""}>
                      <td>{r.row}</td>
                      <td>{r.title}</td>
                      <td className="admin-slug">/{r.slug}</td>
                      <td><span className={`badge badge-${asDraft ? "draft" : r.status}`}>{asDraft ? "draft" : r.status}</span></td>
                      <td>{(r.categories || []).join(", ")}</td>
                      <td>{r.has_body ? "✓" : "—"}</td>
                      <td>
                        {r.duplicate ? (
                          <span className="admin-dup-tag" title={r.duplicate_reason}>
                            {skipDuplicates ? "Duplicate — will skip" : "Duplicate"}
                          </span>
                        ) : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.error_count ? (
                <ul className="admin-import-errors" data-testid="preview-errors">
                  {preview.errors.map((er, i) => (<li key={i}>Row {er.row}: {er.error}</li>))}
                </ul>
              ) : null}
            </div>

            <div className="admin-modal-foot">
              <button className="btn-ghost" onClick={cancelPreview} data-testid="preview-cancel-btn">Cancel</button>
              <button className="btn-primary" disabled={importing || preview.valid_count === 0} onClick={confirmImport} data-testid="preview-confirm-btn">
                {importing
                  ? "Importing…"
                  : (() => {
                      const n = skipDuplicates ? preview.valid_count - (preview.duplicate_count || 0) : preview.valid_count;
                      return `Create ${n} post${n === 1 ? "" : "s"}`;
                    })()}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
